import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Calendar,
    Clock,
    Users,
    CheckCircle,
    XCircle,
    Filter,
    ChevronLeft,
    ChevronRight,
    FileText,
    UserCheck,
} from "lucide-react";
import "./AgendaManager.css";

import { fetchParamCongesRequest } from "../../../redux/actions/paramCongesActions";
import { getDirectReportsRequest } from "../../../redux/actions/userActions";
import { getManagerTeamLeavesRequest } from "../../../redux/actions/leaveRequestsActions";

const AgendaManager = () => {
    const dispatch = useDispatch();
    const uid = sessionStorage.getItem("uid");

    const [activeTab, setActiveTab] = useState("agenda");
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedCollaborator, setSelectedCollaborator] = useState("all");
    const [selectedType, setSelectedType] = useState("all");
    const [selectedStatus, setSelectedStatus] = useState("all");

    // === REDUX STATE ===
    const { typesAbsences } = useSelector((state) => state.paramConges);
    const { directReports, directReportsLoading } = useSelector((state) => state.user);
    const { teamLeaves, loading: leavesLoading } = useSelector((state) => state.leaveRequests);

    useEffect(() => {
        dispatch(fetchParamCongesRequest());
        if (uid) {
            dispatch(getDirectReportsRequest(uid));
        }
        dispatch(getManagerTeamLeavesRequest());
    }, [dispatch, uid]);

    // === MAPPING DATA ===
    const collaborateurs = useMemo(() => {
        return (directReports || []).map(u => ({
            id: u.id,
            nom: `${u.firstName} ${u.lastName}`,
            avatar: `${u.firstName?.charAt(0) || ""}${u.lastName?.charAt(0) || ""}`
        }));
    }, [directReports]);

    const conges = useMemo(() => {
        return (teamLeaves || []).map(l => {
            // Mapping status
            let statut = "en_attente";
            if (l.status === "APPROVED") statut = "validé";
            if (l.status === "REJECTED") statut = "refusé";
            if (l.status === "CANCELLED") statut = "annulé";

            return {
                id: l.id,
                collaborateurId: l.employeeId,
                collaborateurNom: l.employeeName,
                type: l.absenceTypeNom,
                debut: l.dateDebut,
                fin: l.dateFin,
                statut: statut,
                motif: l.commentaire,
                duree: l.duree
            };
        });
    }, [teamLeaves]);

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const days = [];
        const startOffset = firstDay === 0 ? 6 : firstDay - 1;

        for (let i = 0; i < startOffset; i++) days.push(null);
        for (let day = 1; day <= daysInMonth; day++) days.push(new Date(year, month, day));
        return days;
    };

    const days = useMemo(() => getDaysInMonth(currentMonth), [currentMonth]);

    const filteredConges = useMemo(() => {
        return conges.filter((c) => {
            if (selectedCollaborator !== "all" && c.collaborateurId !== parseInt(selectedCollaborator)) return false;
            if (selectedType !== "all" && c.type !== selectedType) return false;
            if (selectedStatus !== "all" && c.statut !== selectedStatus) return false;
            return true;
        });
    }, [selectedCollaborator, selectedType, selectedStatus]);

    const getCongesForDate = (date) => {
        if (!date) return [];
        const dateStr = date.toISOString().split("T")[0];
        return filteredConges.filter((c) => c.debut <= dateStr && c.fin >= dateStr);
    };

    const getTypeColor = (typeNom) => {
        const type = (typesAbsences || []).find((t) => t.nom === typeNom);
        return type?.couleur || "#6b7280";
    };

    const changeMonth = (increment) => {
        setCurrentMonth((prev) => {
            const newDate = new Date(prev);
            newDate.setMonth(prev.getMonth() + increment);
            return newDate;
        });
    };

    const monthName = currentMonth.toLocaleString("fr-FR", { month: "long", year: "numeric" });

    const tabs = [
        { id: "agenda", label: "Agenda", icon: Calendar },
        { id: "liste", label: "Liste des congés", icon: FileText },
    ];

    return (
        <div className="amPage">
            <header className="amHeader">
                <div>
                    <h1>Agenda des congés</h1>
                    <p>Suivez les absences et validez les demandes de vos collaborateurs</p>
                </div>
                {(directReportsLoading || leavesLoading) && <div className="amLoader">Chargement...</div>}
            </header>

            <div className="amCard">
                {/* Tabs */}
                <div className="amTabs">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                type="button"
                                className={`amTab ${activeTab === tab.id ? "is-active" : ""}`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                <Icon size={18} />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                <div className="amBody">
                    {/* Filtres */}
                    <div className="amFilters">
                        <div className="amFilters__label">
                            <Filter size={16} />
                            <span>Filtres :</span>
                        </div>

                        <select
                            value={selectedCollaborator}
                            onChange={(e) => setSelectedCollaborator(e.target.value)}
                            className="amSelect"
                        >
                            <option value="all">Tous les collaborateurs</option>
                            {collaborateurs.map((collab) => (
                                <option key={collab.id} value={collab.id}>
                                    {collab.nom}
                                </option>
                            ))}
                        </select>

                        <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)} className="amSelect">
                            <option value="all">Tous les types</option>
                            {(typesAbsences || []).map((type) => (
                                <option key={type.id} value={type.nom}>
                                    {type.nom}
                                </option>
                            ))}
                        </select>

                    </div>

                    {/* === AGENDA === */}
                    {activeTab === "agenda" && (
                        <div>
                            {/* Navigation mois */}
                            <div className="amMonthBar">
                                <h2 className="amMonthTitle">
                                    {monthName.charAt(0).toUpperCase() + monthName.slice(1)}
                                </h2>

                                <div className="amMonthActions">
                                    <button type="button" className="amIconCircle" onClick={() => changeMonth(-1)} title="Mois précédent">
                                        <ChevronLeft size={20} />
                                    </button>

                                    <button type="button" className="amTodayBtn" onClick={() => setCurrentMonth(new Date())}>
                                        Aujourd&apos;hui
                                    </button>

                                    <button type="button" className="amIconCircle" onClick={() => changeMonth(1)} title="Mois suivant">
                                        <ChevronRight size={20} />
                                    </button>
                                </div>
                            </div>

                            {/* Grille calendrier */}
                            <div className="amCalendar">
                                <div className="amWeekHeader">
                                    {["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"].map((day) => (
                                        <div key={day} className="amWeekDay">
                                            {day}
                                        </div>
                                    ))}
                                </div>

                                <div className="amGrid">
                                    {days.map((date, idx) => {
                                        const congesDuJour = date ? getCongesForDate(date) : [];
                                        const isToday = date && date.toDateString() === new Date().toDateString();
                                        const dayNumber = date ? date.getDate() : "";

                                        return (
                                            <div
                                                key={idx}
                                                className={`amCell ${!date ? "is-empty" : ""} ${isToday ? "is-today" : ""}`}
                                            >
                                                {date && (
                                                    <>
                                                        <div className={`amDayNum ${isToday ? "is-today" : ""}`}>{dayNumber}</div>

                                                        <div className="amCellList">
                                                            {congesDuJour.map((conge, i) => {
                                                                const type = (typesAbsences || []).find((t) => t.nom === conge.type);
                                                                const displayName = conge.collaborateurNom || "";

                                                                return (
                                                                    <div
                                                                        key={`${conge.id}-${i}`}
                                                                        className="amChip"
                                                                        style={{ backgroundColor: getTypeColor(conge.type) }}
                                                                        title={`${type?.nom || conge.type} — ${displayName} (${conge.statut})`}
                                                                    >
                                                                        <UserCheck size={12} />
                                                                        <span className="amChip__code">{type?.code || "???"}</span>
                                                                        <span className="amChip__sep">—</span>
                                                                        <span className="amChip__name">{displayName}</span>
                                                                    </div>
                                                                );
                                                            })}

                                                            {congesDuJour.length > 3 && (
                                                                <div className="amMore">
                                                                    +{congesDuJour.length - 3} autre{congesDuJour.length - 3 > 1 ? "s" : ""}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Légende */}
                            <div className="amLegend">
                                <span className="amLegend__title">Légende :</span>

                                <div className="amLegend__items">
                                    {(typesAbsences || []).map((type) => (
                                        <div key={type.id} className="amLegendItem">
                                            <div className="amLegendDot" style={{ backgroundColor: type.couleur }} />
                                            <span>{type.nom}</span>
                                        </div>
                                    ))}
                                </div>

                                <div className="amLegendHint">
                                    <Users size={16} />
                                    <span>Nom complet affiché</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* === LISTE === */}
                    {activeTab === "liste" && (
                        <div className="amTableWrap">
                            <table className="amTable">
                                <thead>
                                <tr>
                                    <th>Collaborateur</th>
                                    <th>Type</th>
                                    <th>Période</th>
                                    <th>Durée</th>
                                    <th>Motif</th>
                                    <th>Statut</th>
                                </tr>
                                </thead>

                                <tbody>
                                {filteredConges.map((conge) => {
                                    const type = (typesAbsences || []).find((t) => t.nom === conge.type);

                                    return (
                                        <tr key={conge.id}>
                                            <td>
                                                <div className="amCollab">
                                                    <div className="amAvatar">
                                                        {conge.collaborateurNom?.split(" ").map(n => n[0]).join("").toUpperCase().substring(0, 2)}
                                                    </div>
                                                    <span className="amCollabName">{conge.collaborateurNom}</span>
                                                </div>
                                            </td>

                                            <td>
                          <span className="amTypeCode" style={{ backgroundColor: getTypeColor(conge.type) }}>
                            {type?.code}
                          </span>
                                            </td>

                                            <td>
                                                {new Date(conge.debut).toLocaleDateString("fr-FR")} → {new Date(conge.fin).toLocaleDateString("fr-FR")}
                                            </td>

                                            <td className="amStrong">
                                                {conge.duree} jour{conge.duree > 1 ? "s" : ""}
                                            </td>

                                            <td className="amMuted">{conge.motif || "-"}</td>

                                            <td>
                                                <div className="amStatus">
                                                    {conge.statut === "validé" && <CheckCircle size={16} className="amOk" />}
                                                    {conge.statut === "en_attente" && <Clock size={16} className="amWait" />}
                                                    {conge.statut === "refusé" && <XCircle size={16} className="amNo" />}
                                                    {conge.statut === "annulé" && <XCircle size={16} className="amCancel" />}
                                                    <span className="amStatusTxt">{conge.statut.replace("_", " ")}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>

                            {filteredConges.length === 0 && <div className="amEmpty">Aucune absence trouvée pour les filtres sélectionnés.</div>}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AgendaManager;
